import { Concern, useExplore } from "@/components/explore/ExploreContext";
import { PlayDetail } from "@/components/playlist/PlayDetail";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import React, { FunctionComponent, useMemo, useState } from "react";

export type TableCellFormatter = (params: {
  zoneName: string;
  timeLabel: string;
  value: number;
  rowIndex: number;
  colIndex: number;
}) => {
  display: React.ReactNode;
  tooltip?: string;
};

function ConcernDisplay({ concern }: { concern: Concern }) {
  return <PlayDetail playId={concern.play_id} />;
}

interface TableViewProps {
  customCellFormatter?: TableCellFormatter;
  onCellClick?: (
    zoneId: number,
    interval: number,
    zoneName: string,
    timeLabel: string,
    zoneIds?: number[]
  ) => void;
  maxHeight?: number;
}

export const TableView: FunctionComponent<TableViewProps> = ({
  customCellFormatter,
  onCellClick,
  maxHeight,
}) => {
  const { zones: rawZones, times, concerns, zoneType } = useExplore();
  const [open, setOpen] = useState(false);
  const [selectedConcern, setSelectedConcern] = useState<Concern | null>(null);

  const zones = useMemo(() => {
    if (zoneType === "door" || zoneType === "apm") {
      return rawZones;
    }
    return [...rawZones].reverse();
  }, [rawZones, zoneType]);

  const { min, max } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    zones.forEach((zone) => {
      (zone.values ?? []).forEach((value) => {
        min = Math.min(min, value);
        max = Math.max(max, value);
      });
    });

    return { min, max };
  }, [zones]);

  // Create concern lookup similar to Heatmap
  const conLookup = useMemo(() => {
    const lookup: { [key: string]: Concern } = {};
    if (concerns && concerns.length > 0) {
      concerns.forEach((c) => {
        const adjustedZoneIndex = zones.length - 1 - c.zone_index;
        lookup[`${adjustedZoneIndex}-${c.time_index}`] = c;
      });
    }
    return lookup;
  }, [concerns, zones.length]);

  const columnTotals = useMemo(
    () =>
      times.map((_, colIndex) =>
        zones.reduce((sum, zone) => sum + (zone.values?.[colIndex] ?? 0), 0)
      ),
    [zones, times]
  );

  const rowTotals = useMemo(
    () =>
      zones.map((zone) =>
        (zone.values ?? []).reduce((sum, value) => sum + (value ?? 0), 0)
      ),
    [zones]
  );

  const formatValue = (target: number, rowIndex: number, colIndex: number) => {
    const concernKey = `${rowIndex}-${colIndex}`;
    const hasConcern = concernKey in conLookup;
    const zone = zones[rowIndex];
    const timeLabel = times[colIndex];

    let ratio = 0;
    const sat = "80";
    const lum = "50";
    if (max != min && target > 0) {
      ratio = (target - min) / (max - min);
    }
    const hue = Math.max(30, (1 - ratio) * 120);
    const color = `hsl(${hue}, ${sat}%, ${lum}%)`;

    const bgOpacity = target === 0 ? 0 : Math.max(0.05, ratio * 0.25);
    const bgColor =
      target === 0 ? "transparent" : `hsla(${hue}, 70%, 50%, ${bgOpacity})`;

    let displayContent: React.ReactNode;
    let tooltipContent: string | undefined;

    if (customCellFormatter) {
      const formatted = customCellFormatter({
        zoneName: zone.name,
        timeLabel,
        value: target,
        rowIndex,
        colIndex,
      });
      tooltipContent = formatted.tooltip;

      if (target === 0) {
        displayContent = (
          <span className={"text-gray-600"}>{formatted.display}</span>
        );
      } else {
        displayContent = <span style={{ color }}>{formatted.display}</span>;
      }
    } else {
      if (target === 0) {
        displayContent = <span className={"text-gray-600"}>{target}</span>;
      } else {
        displayContent = (
          <span style={{ color }}>{target.toLocaleString()}</span>
        );
      }
    }

    const content = (
      <span
        className={`${hasConcern ? "bg-red-900 px-2 py-1 rounded cursor-pointer" : "px-2 py-1 rounded cursor-pointer"}`}
        style={{
          backgroundColor: hasConcern ? undefined : bgColor,
        }}
        onClick={() => {
          if (hasConcern) {
            setSelectedConcern(conLookup[concernKey]);
            setOpen(true);
          } else if (zone.id && onCellClick) {
            onCellClick(zone.id, colIndex, zone.name, timeLabel, zone.zone_ids);
          }
        }}
      >
        {displayContent}
      </span>
    );

    if (tooltipContent) {
      return (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent>
              <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  return (
    <>
      <div
        className="w-full overflow-auto text-xs font-mono rounded-lg"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className="border-separate border-spacing-0 whitespace-nowrap">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-40 p-2 h-10 text-left bg-slate-800 text-slate-300 text-xs min-w-[8rem] max-w-[24rem]">
                Name
              </th>
              {times.map((label, i) => (
                <th
                  className="sticky top-0 z-30 p-2 h-10 text-center bg-slate-800 text-slate-300 text-xs min-w-[3.5rem]"
                  key={i}
                >
                  {label}
                </th>
              ))}
              <th className="sticky top-0 right-0 z-40 p-2 h-10 text-left bg-slate-800 text-slate-300 text-xs min-w-[4rem]">
                Totals
              </th>
            </tr>
          </thead>
          <tbody>
            {zones.map((zone, rowIndex) => (
              <tr key={rowIndex}>
                <td
                  className="sticky left-0 z-20 py-1 px-2 h-8 bg-slate-900 max-w-[24rem] truncate border-r border-slate-700"
                  title={zone.name}
                >
                  {zone.name}
                </td>
                {times.map((_, colIndex) => (
                  <td
                    className="p-1 text-center h-8 min-w-[3.5rem] bg-slate-900"
                    key={colIndex}
                  >
                    {formatValue(
                      zone.values?.[colIndex] ?? 0,
                      rowIndex,
                      colIndex
                    )}
                  </td>
                ))}
                <td className="sticky right-0 z-20 py-1 px-2 h-8 bg-slate-900 text-slate-400 border-l border-slate-700">
                  {rowTotals[rowIndex].toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td className="sticky bottom-0 left-0 z-40 py-1 px-2 h-8 bg-slate-800 border-r border-slate-700">
                Total
              </td>
              {columnTotals.map((total, colIndex) => (
                <td
                  className="sticky bottom-0 z-30 p-1 text-center h-8 min-w-[3.5rem] bg-slate-800"
                  key={colIndex}
                >
                  {total.toLocaleString("en-US")}
                </td>
              ))}
              <td className="sticky bottom-0 right-0 z-40 py-1 px-2 h-8 bg-slate-800 border-l border-slate-700">
                {columnTotals
                  .reduce((sum, total) => sum + total, 0)
                  .toLocaleString("en-US")}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alert Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(100vh-250px)]">
            {selectedConcern && <ConcernDisplay concern={selectedConcern} />}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};
